import type { SimulatableOrder } from "@opus-dt/shared-types";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  CreateProductionOrderRequest,
  UpdateProductionOrderRequest,
  CancelProductionOrderRequest,
  OperatingCalendar,
} from "@opus-dt/shared-types";
import type {
  ProductionOrder,
  ProductionOrderListResponse,
  ProductionOrderIndicators,
  ProductionOrderFilters,
} from "../../types/production-orders.types.js";

export type {
  ProductionOrder,
  ProductionOrderListResponse,
  ProductionOrderIndicators,
  CreateProductionOrderRequest,
  UpdateProductionOrderRequest,
  CancelProductionOrderRequest,
  ProductionOrderQueryRequest,
  ProductionOrderFilters,
} from "../../types/production-orders.types.js";

const QK = ["production-orders"] as const;

export function useProductionOrders(filters: ProductionOrderFilters = {}) {
  return useQuery<ProductionOrderListResponse>({
    queryKey: [...QK, filters],
    queryFn: () => {
      const p = new URLSearchParams();
      if (filters.lineId) p.set("lineId", filters.lineId);
      if (filters.status) p.set("status", filters.status);
      if (filters.responsible) p.set("responsible", filters.responsible);
      if (filters.from) p.set("from", filters.from);
      if (filters.to) p.set("to", filters.to);
      if (filters.overlap) p.set("overlap", "true");
      if (filters.lateOnly) p.set("lateOnly", "true");
      if (filters.offset != null) p.set("offset", String(filters.offset));
      if (filters.limit != null) p.set("limit", String(filters.limit));
      const qs = p.toString();
      return api.get<ProductionOrderListResponse>(`/api/v1/production-orders${qs ? `?${qs}` : ""}`);
    },
  });
}

export function useProductionOrderIndicators(id: string) {
  return useQuery<ProductionOrderIndicators>({
    queryKey: [...QK, id, "indicators"],
    queryFn: () => api.get<ProductionOrderIndicators>(`/api/v1/production-orders/${id}/indicators`),
    enabled: !!id,
  });
}

export function useProductionOrder(id: string) {
  return useQuery<ProductionOrder>({
    queryKey: [...QK, id],
    queryFn: () => api.get<ProductionOrder>(`/api/v1/production-orders/${id}`),
    enabled: !!id,
  });
}

// Calendários das linhas visíveis na timeline (uma query por linha, chave
// compartilhada com `lines/calendar/hooks.ts` ⇒ cache reusado). Retorna mapa lineId → calendário|null
// para as faixas "fora do expediente". Nº de linhas por faixa é pequeno (swimlanes), sem N+1 pesado.
export function useLineCalendars(lineIds: string[]): Record<string, OperatingCalendar | null> {
  const results = useQueries({
    queries: lineIds.map((lineId) => ({
      queryKey: ["lines", lineId, "calendar"] as const,
      queryFn: () => api.get<OperatingCalendar | null>(`/api/v1/lines/${lineId}/calendar`),
      enabled: !!lineId,
    })),
  });
  const map: Record<string, OperatingCalendar | null> = {};
  lineIds.forEach((lineId, i) => {
    map[lineId] = results[i]?.data ?? null;
  });
  return map;
}

export function useCreateProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProductionOrderRequest) =>
      api.post<ProductionOrder>("/api/v1/production-orders", data),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: ["lines", created.lineId] });
    },
    // Consumidor `ProductionOrderForm.tsx` já trata localmente.
    meta: { skipErrorToast: true },
  });
}

export function useUpdateProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateProductionOrderRequest }) =>
      api.patch<ProductionOrder>(`/api/v1/production-orders/${id}`, data),
    onSuccess: (updated) => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: [...QK, updated.id] });
    },
    // Consumidor `ProductionOrderForm.tsx` já trata localmente.
    meta: { skipErrorToast: true },
  });
}

export function useCancelProductionOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: CancelProductionOrderRequest }) =>
      api.post<ProductionOrder>(`/api/v1/production-orders/${id}/cancel`, data ?? {}),
    onSuccess: (cancelled) => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: [...QK, cancelled.id] });
    },
    // Consumidor `production-orders/index.tsx` já trata localmente.
    meta: { skipErrorToast: true },
  });
}

// Aplica a sequência escolhida: reagenda a fila inteira numa transação.
// Invalida as OPs (janelas novas) e a linha (a agenda dela mudou).
export function useApplySequence(lineId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (simulationId: string) =>
      api.post<{ orderIds: string[] }>("/api/v1/production-orders/apply-sequence", {
        simulationId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["production-orders"] });
      if (lineId) void qc.invalidateQueries({ queryKey: ["lines", lineId] });
    },
    // O drawer ramifica por código (SEQUENCE_STALE ⇒ CTA "simular de novo") — trata local.
    meta: { skipErrorToast: true },
  });
}

// Ordens que o objetivo "entregar no prazo" consegue avaliar. O saldo de uma OP
// em andamento muda a cada apontamento, então esta chave é invalidada por toda mutação de produção
// (ver command-effect-keys e useRealtimeInvalidate): oferecer um saldo velho faria a resposta
// prometer entrega sobre produção desatualizada.
export function useSimulatableOrders(lineId: string | null) {
  return useQuery({
    queryKey: ["production-orders", lineId, "simulatable"],
    queryFn: () =>
      api.get<{ items: SimulatableOrder[] }>(
        `/api/v1/production-orders/lines/${lineId}/simulatable`
      ),
    enabled: !!lineId,
    select: (r) => r.items,
  });
}
